import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import logoUrl from '../assets/tadbeer-queue-logo.png';

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Shared auth shell. The left panel carries a Tadbeer-inspired service-centre
 * identity — a light teal panel with the Tadbeer Queue logo — so every auth
 * screen (login, invite, password reset) reads as one government-style
 * service portal.
 */
export function AuthCard({
  title,
  subtitle,
  children,
  footer,
}: Props): JSX.Element {
  const { t } = useTranslation('auth');
  return (
    <main className="flex min-h-screen w-full bg-background text-foreground">
      <aside
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-[hsl(187_56%_96%)] via-[hsl(190_50%_92%)] to-[hsl(194_52%_86%)] px-10 py-12 text-[hsl(193_45%_22%)] lg:flex"
        aria-hidden="true"
      >
        {/* Soft teal glow so the light panel doesn't read as a flat wash. */}
        <div className="pointer-events-none absolute -top-24 end-[-6rem] h-80 w-80 rounded-full bg-[hsl(189_85%_55%)]/25 blur-3xl" />
        <div className="relative">
          <BrandMark />
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(192_58%_34%)]">
            {t('shell.panel_eyebrow')}
          </p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight text-[hsl(194_72%_15%)]">
            {t('shell.panel_title')}
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[hsl(193_26%_34%)]">
            {t('shell.panel_body')}
          </p>
        </div>
        <ul className="relative mt-12 space-y-3 text-sm">
          <li className="flex items-start gap-2.5">
            <BulletDot />
            <span className="text-[hsl(193_28%_32%)]">
              {t('shell.panel_bullet_one')}
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <BulletDot />
            <span className="text-[hsl(193_28%_32%)]">
              {t('shell.panel_bullet_two')}
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <BulletDot />
            <span className="text-[hsl(193_28%_32%)]">
              {t('shell.panel_bullet_three')}
            </span>
          </li>
        </ul>
        <div className="relative">
          <div className="mb-4 h-px w-16 bg-[hsl(190_55%_45%)]/50" />
          <p className="text-xs text-[hsl(193_20%_46%)]">{t('shell.footer')}</p>
        </div>
      </aside>
      <section className="flex w-full items-center justify-center px-4 py-10 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <div className="mb-5 lg:hidden">
              <BrandMark compact />
            </div>
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            {subtitle ? (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            {children}
          </div>
          {footer ? (
            <div className="mt-4 text-sm text-muted-foreground">{footer}</div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

/** Tadbeer Queue brand lockup, rendered straight from the transparent PNG. */
function BrandMark({ compact = false }: { compact?: boolean }): JSX.Element {
  return (
    <img
      src={logoUrl}
      alt="Tadbeer Queue"
      className={compact ? 'h-14 w-auto' : 'h-28 w-auto'}
    />
  );
}

function BulletDot(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(190_72%_40%)]"
    />
  );
}
